/**
 * DiceTypeSettings - Orchestrates provider configuration for all registered dice.
 *
 * On mount, fetches provider configs for each registered dice type.
 * Provider changes are debounced 500ms before calling updateProviderConfig.
 * Shows empty state with link to /register when no dice are registered.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { DiceType, RecognitionProvider } from '@dice-capture/shared/types/dice-registry';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDiceRegistryStore } from '@/stores/diceRegistryStore';
import { useProviderConfigStore } from '@/stores/providerConfigStore';
import { DiceTypeCard } from './DiceTypeCard';

const SERVICE_TAG = '[DICE_SETTINGS]';

const DEBOUNCE_MS = 500;

/**
 * Renders provider configuration settings for all registered dice types.
 * Fetches provider configs on mount and debounces save calls.
 */
export function DiceTypeSettings() {
  const navigate = useNavigate();
  const { registrations, deviceId } = useDiceRegistryStore();
  const { providerConfigs, fetchProviderConfig, updateProviderConfig } = useProviderConfigStore();

  /** Track in-flight fetch states per diceType */
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  /** Track in-flight save (PUT) states per diceType — drives the "Saving…" indicator */
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});
  /** Debounce timer refs keyed by diceType */
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Fetch provider configs for all registrations on mount
  useEffect(() => {
    if (!deviceId || registrations.length === 0) return;

    registrations.forEach((reg) => {
      // Use the registration's deviceId for cross-device registrations
      const regDeviceId = reg.deviceId || deviceId;
      const key = `${regDeviceId}#${reg.diceType}`;
      const existing = providerConfigs[key];
      // Skip fetch only if config has availableProviders (bulk load from /my-registrations omits them)
      if (existing?.availableProviders?.length) return;

      setLoadingMap((prev) => ({ ...prev, [reg.diceType]: true }));
      fetchProviderConfig(regDeviceId, reg.diceType).finally(() => {
        setLoadingMap((prev) => ({ ...prev, [reg.diceType]: false }));
      });
    });
  }, [deviceId, registrations, fetchProviderConfig, providerConfigs]);

  /**
   * Handle provider change for a dice type.
   * Debounces the API call by DEBOUNCE_MS milliseconds.
   */
  const handleProviderChange = useCallback(
    (diceType: DiceType, newActiveProviders: RecognitionProvider[]) => {
      if (!deviceId) return;

      // Clear any pending debounce for this diceType
      if (debounceRefs.current[diceType]) {
        clearTimeout(debounceRefs.current[diceType]);
      }

      debounceRefs.current[diceType] = setTimeout(async () => {
        setSavingMap((prev) => ({ ...prev, [diceType]: true }));
        try {
          await updateProviderConfig(deviceId, diceType, newActiveProviders);
          toast.success('Provider settings saved');
          console.info(`${SERVICE_TAG} Provider config saved`, { deviceId, diceType });
        } catch (error) {
          toast.error('Failed to save provider settings');
          console.error(`${SERVICE_TAG} Provider config save failed`, {
            deviceId,
            diceType,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        } finally {
          setSavingMap((prev) => ({ ...prev, [diceType]: false }));
        }
      }, DEBOUNCE_MS);
    },
    [deviceId, updateProviderConfig]
  );

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceRefs.current).forEach(clearTimeout);
    };
  }, []);

  if (registrations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground mb-4">No dice registered yet.</p>
          <Button
            variant="accent"
            onClick={() => navigate('/register')}
            enablePressEffect
          >
            Register Dice
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {registrations.map((registration) => {
        const regDeviceId = registration.deviceId || deviceId;
        const key = regDeviceId ? `${regDeviceId}#${registration.diceType}` : '';
        const providerConfig = key ? providerConfigs[key] : undefined;
        const isLoading = loadingMap[registration.diceType] ?? false;
        const isSaving = savingMap[registration.diceType] ?? false;

        return (
          <DiceTypeCard
            key={registration.diceType}
            registration={registration}
            providerConfig={providerConfig}
            isLoading={isLoading}
            isSaving={isSaving}
            onProviderChange={(newProviders) =>
              handleProviderChange(registration.diceType, newProviders)
            }
          />
        );
      })}
    </div>
  );
}
